/**
 * Trust-on-first-use for the venue's contract addresses, per chain.
 *
 * The half of `domain/chain-trust.ts` that needs storage, kept out of it so
 * that module stays a pure function of its inputs and testable without a DOM
 * (invariant 10).
 *
 * **It covers the deployments with no pin, and it covers them honestly.** A
 * first visit is trusted and remembered; a later change is refused until a
 * person looks at both addresses and says so. What it cannot do is protect a
 * first load — a browser with nothing stored has nothing to compare against —
 * and `verifyChainTargets` reports that as `basis: 'UNPINNED'` rather than
 * letting the screen imply otherwise. `VITE_UD_CHAIN_PINS` is the answer for a
 * deployment that wants the guarantee.
 *
 * Per browser, like the theme and the chart timeframe. Every read and write is
 * wrapped: a private window, cleared site data or a browser refusing storage
 * must degrade to "first use", never throw on the path to a deposit screen.
 */

import type { ChainPin } from '@/domain/chain-trust'

const KEY = 'ud.chain-trust.v1'

type Store = Record<string, ChainPin>

function read(): Store {
  try {
    const raw = globalThis.localStorage?.getItem(KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    return typeof parsed === 'object' && parsed !== null ? (parsed as Store) : {}
  } catch {
    return {}
  }
}

/** What this browser trusted for `chainId`, or `null` for a first visit. */
export function seenPin(chainId: number): ChainPin | null {
  return read()[String(chainId)] ?? null
}

/**
 * Remember this triple as the trusted one for `chainId`.
 *
 * Called on a **successful** verdict and on an explicit confirmation of a
 * change, and nowhere else — writing it before the verdict would make the
 * store agree with whatever it had just been shown, which is the one thing it
 * exists not to do.
 */
export function rememberPin(chainId: number, pin: ChainPin): void {
  try {
    const store = read()
    store[String(chainId)] = pin
    globalThis.localStorage?.setItem(KEY, JSON.stringify(store))
  } catch {
    // Storage refused. The next visit is a first visit, which is the safe
    // direction: it asks again rather than trusting silently.
  }
}

/** Forget one chain's trusted triple. For the tests, and for a user who wants
 *  to be asked again. */
export function forgetPin(chainId: number): void {
  try {
    const store = read()
    delete store[String(chainId)]
    globalThis.localStorage?.setItem(KEY, JSON.stringify(store))
  } catch {
    /* nothing stored is the state this wanted */
  }
}
